// Display helpers — currency, slugs, image lookups, display-name overrides.

import type { Product } from "./api";
import { RULES } from "./rules";

/** Prepend the asset base URL (set by Vite's `base` config) to a
 *  same-origin asset path. In dev BASE_URL is "/", so this returns the
 *  path unchanged. In production BASE_URL is the full droplet URL, so
 *  this returns an absolute https:// URL — required when the bundle is
 *  embedded cross-origin from gammill.com/build/ (where a leading "/"
 *  would point at gammill.com itself, not the asset host). */
export function assetUrl(pathFromRoot: string): string {
  const base = import.meta.env.BASE_URL; // always ends in "/"
  // Tolerate either "/foo" or "foo" — strip a leading slash if present
  // and let base supply the slash.
  const tail = pathFromRoot.startsWith("/") ? pathFromRoot.slice(1) : pathFromRoot;
  return `${base}${tail}`;
}

/** Build a URL-safe filename slug from a product name —
 *  "Statler USB Keyboard (130-0013)" → "statler-usb-keyboard-130-0013". */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Customer-facing name: rules.displayName overrides the HubSpot name. */
export function displayNameFor(p: Product): string {
  return RULES[p.id]?.displayName ?? p.name;
}

/** Customer-facing description: the app (rules.ts) is the source of truth,
 *  falling back to HubSpot's description only when we have no override. */
export function descriptionFor(p: Product): string {
  return RULES[p.id]?.description ?? p.description;
}

/** Shown when the delivery address is outside the contiguous lower-48 — the tool
 *  stops quoting and routes the customer to Sales for a manual shipping quote. */
export const OUTSIDE_LOWER48_MESSAGE =
  "Please contact Gammill Sales at 417-256-5919 or sales@gammill.com, your " +
  "location may or may not have additional shipping expense";

// AK / HI / PR / VI / GU / Pacific-territory ZIP prefixes — outside the lower-48.
const NON_LOWER48_ZIP3 = new Set([
  "006", "007", "008", "009", "967", "968", "969",
  "995", "996", "997", "998", "999",
]);

/** Live (client-side) lower-48 check by ZIP shape + prefix. Non-US postal codes
 *  (Canada, international) and AK/HI/territory ZIPs return false. A 5-digit
 *  international ZIP can slip past here but the backend (which also uses the
 *  state) rejects it on Calculate. */
export function isLower48Zip(zip: string): boolean {
  const z = (zip || "").trim().replace(/\s/g, "");
  if (!/^\d{5}(-\d{4})?$/.test(z)) return false;
  return !NON_LOWER48_ZIP3.has(z.slice(0, 3));
}

/** App-controlled product video (Vimeo or YouTube). When set, the detail card
 *  shows the video in place of the image. */
export function videoUrlFor(p: Product): string | undefined {
  return RULES[p.id]?.videoUrl;
}

/** Whether the item's video should autoplay (muted). Off by default — most
 *  videos show a poster + play button and wait for a click. */
export function videoAutoplayFor(p: Product): boolean {
  return Boolean(RULES[p.id]?.videoAutoplay);
}

/** Self-hosted product video sources, when the item has one (rules.localVideo).
 *  Derived from the same slug as images: `<slug>.webm` + `<slug>.mp4`, poster
 *  `<slug>.jpg`. Returns null when there's no local video. */
export function localVideoFor(
  p: Product,
): { webm: string; mp4: string; poster: string } | null {
  if (!RULES[p.id]?.localVideo) return null;
  const slug = RULES[p.id]?.imageSlug ?? slugify(p.name);
  return {
    webm: assetUrl(`images/products/${slug}.webm`),
    mp4: assetUrl(`images/products/${slug}.mp4`),
    poster: assetUrl(`images/products/${slug}.jpg`),
  };
}

/** Convert a Vimeo or YouTube URL into its player-embed URL. Accepts Vimeo
 *  share/player links and YouTube watch/share/embed/shorts links. When
 *  `autoplay` is set, the embed autoplays MUTED (the only kind browsers allow).
 *  Returns null if it isn't a recognizable video link. */
export function videoEmbedUrl(url: string, autoplay = false): string | null {
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-f]+))?/i);
  if (vimeo) {
    const params = new URLSearchParams();
    const hash = vimeo[2] ?? url.match(/[?&]h=([0-9a-f]+)/i)?.[1];
    if (hash) params.set("h", hash);
    if (autoplay) {
      params.set("autoplay", "1");
      params.set("muted", "1");
    }
    const q = params.toString();
    return `https://player.vimeo.com/video/${vimeo[1]}${q ? `?${q}` : ""}`;
  }
  const yt = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/i,
  );
  if (yt) {
    const params = new URLSearchParams({ rel: "0" });
    if (autoplay) {
      params.set("autoplay", "1");
      params.set("mute", "1");
      params.set("playsinline", "1");
    }
    return `https://www.youtube.com/embed/${yt[1]}?${params.toString()}`;
  }
  return null;
}

/** Ordered list of image URLs to try for a product. The Thumb / Hero
 *  components walk the list, falling back to the next URL on 404, and
 *  finally to a hatched placeholder if all fail.
 *
 *  Resolution order (app is the source of truth; HubSpot is the fallback):
 *    1. /images/products/<imageSlug>.{webp,jpg,png,gif}  (rules-overridden slug)
 *    2. /images/products/<slugify(name)>.{webp,jpg,png,gif}  (auto-derived)
 *    3. HubSpot's image_url (only if we have no local file)
 *
 *  All four extensions are tried so contributors can save in whichever
 *  format makes sense (jpgs for photos, pngs for cutouts, webp/gif for
 *  animation).
 */
export function imageCandidatesFor(p: Product): string[] {
  const out: string[] = [];
  const rule = RULES[p.id];
  const slugs = [rule?.imageSlug, slugify(p.name)].filter(
    (s, i, arr): s is string => Boolean(s) && arr.indexOf(s) === i,
  );
  // Bundled images first. Walks extensions in priority order: animated WebP
  // first so a newer animation supersedes an older static fallback for the
  // same slug; static formats next; GIF last (heaviest at equivalent quality,
  // so prefer modern formats when both exist).
  for (const slug of slugs) {
    out.push(assetUrl(`images/products/${slug}.webp`));
    out.push(assetUrl(`images/products/${slug}.jpg`));
    out.push(assetUrl(`images/products/${slug}.png`));
    out.push(assetUrl(`images/products/${slug}.gif`));
  }
  // HubSpot ecommerce-bridge image — fallback only when no local file exists.
  if (p.image_url) out.push(p.image_url);
  return out;
}

/** Image candidates for the small thumbnail. When a product has a
 *  `thumbImageSlug` (a crop tailored to the tiny square — e.g. one panel of a
 *  wide comparison poster), those are tried first, then the normal candidates.
 *  Otherwise identical to `imageCandidatesFor`. */
export function thumbCandidatesFor(p: Product): string[] {
  const slug = RULES[p.id]?.thumbImageSlug;
  if (!slug) return imageCandidatesFor(p);
  const out = [
    assetUrl(`images/products/${slug}.webp`),
    assetUrl(`images/products/${slug}.jpg`),
    assetUrl(`images/products/${slug}.png`),
    assetUrl(`images/products/${slug}.gif`),
  ];
  return [...out, ...imageCandidatesFor(p)];
}

export function formatDollars(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatDollarsAndCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Estimated monthly payment for a financed amount, using the standard
 *  fixed-rate amortization formula:
 *
 *      M = P · r(1+r)ⁿ / ((1+r)ⁿ − 1)
 *
 *  where P is the principal (subtotal + tax − down), r is the monthly
 *  rate (APR / 12 / 100), and n is the term in months.
 *
 *  Returns cents (integer). Zero-or-negative principal returns 0 — the
 *  customer's down payment already covers the build, so monthly is $0.
 */
export function amortizedMonthlyCents(
  principalCents: number,
  aprPercent: number,
  termMonths: number,
): number {
  if (principalCents <= 0) return 0;
  if (termMonths <= 0) return principalCents;
  const r = aprPercent / 100 / 12;
  // Zero-APR edge case (defensive — Gammill's tiers are all > 0): plain
  // even-split avoids dividing by zero in the formula below.
  if (r === 0) return Math.round(principalCents / termMonths);
  const pow = Math.pow(1 + r, termMonths);
  const monthly = (principalCents * r * pow) / (pow - 1);
  return Math.round(monthly);
}

/** Format cents as "$529 /mo" — matches the Tesla configurator's per-
 *  trim monthly display. Drops cents (monthly payment estimates round to
 *  the dollar so the customer isn't anchoring on false precision). */
export function formatMonthly(cents: number): string {
  return `${formatDollars(cents)} /mo`;
}
